import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package, ShoppingCart, Plus, Scan, FileText, UserCheck,
  BarChart3, BookOpen, TrendingUp, DollarSign, Users, Heart, Coins,
  GalleryHorizontal, FileSignature, Sparkles, CalendarClock, LifeBuoy, Megaphone, Tag,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAdminDataBatch } from '../../hooks/useAdminData';
import { AdminSkeletonGrid, AdminQuickLink } from '../../components/admin/AdminPrimitives';
import {
  PendingUsersAlert, SlowMovingSection, StatsGrid,
  RecentOrdersPanel, LowStockPanel,
} from '../../components/admin/DashboardSections';
import { CataloguePdfModal } from '../../components/admin/CataloguePdfModal';

const AdminDashboard = () => {
  const { isAdmin, isStaff, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);

  useEffect(() => {
    if (!isStaff) navigate('/connexion');
  }, [isStaff, navigate]);

  const { data, loading } = useAdminDataBatch([
    '/api/admin/stats',
    '/api/admin/slow-moving',
  ]);
  const stats = data['/api/admin/stats'];
  const slowMoving = data['/api/admin/slow-moving'];

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">
              {isAdmin ? 'Administration' : 'Espace Employé'}
            </p>
            <h1 className="text-3xl text-white font-bold"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Tableau de Bord
            </h1>
          </div>

          <div className="flex gap-3 flex-wrap">
            <button type="button" onClick={() => setShowCatalogueModal(true)}
              className="flex items-center gap-2 border border-[color:rgb(var(--primary-rgb)/0.30)] text-[color:var(--color-primary)] px-4 py-2 hover:bg-[color:var(--color-primary)] hover:text-black transition-all text-sm"
              data-testid="download-catalogue-pdf-btn">
              <BookOpen className="w-4 h-4" />
              Catalogue PDF
            </button>
            <Link to="/admin/preparation"
              className="flex items-center gap-2 border border-[color:var(--color-primary)] text-[color:var(--color-primary)] px-4 py-2 hover:bg-[color:var(--color-primary)] hover:text-black transition-all text-sm"
              data-testid="preparation-btn">
              <Scan className="w-4 h-4" />
              Comptoir POS
            </Link>
            <Link to="/admin/produits/nouveau"
              className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 hover:bg-[color:var(--color-primary-hover)] transition-colors text-sm"
              data-testid="add-product-btn">
              <Plus className="w-4 h-4" />
              Nouveau Produit
            </Link>
            {isAdmin && (
              <Link to="/admin/statistiques"
                className="flex items-center gap-2 border border-white/10 text-zinc-400 hover:text-white px-4 py-2 transition-colors text-sm"
                data-testid="stats-link">
                <BarChart3 className="w-4 h-4" />
                Statistiques
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin/analytics"
                className="flex items-center gap-2 border border-[color:rgb(var(--primary-rgb)/0.30)] text-[color:var(--color-primary)] px-4 py-2 hover:bg-[color:var(--color-primary)] hover:text-black transition-colors text-sm"
                data-testid="analytics-link">
                <TrendingUp className="w-4 h-4" />
                Analytics
              </Link>
            )}
          </div>
        </div>

        {loading ? (
          <AdminSkeletonGrid />
        ) : (
          <>
            {isAdmin && stats?.pending_users > 0 && (
              <PendingUsersAlert count={stats.pending_users} />
            )}

            {hasPermission('promo') && stats?.promo_reminder && (
              <Link to="/admin/promotions"
                className="mb-6 flex items-center gap-3 border border-red-500/40 bg-red-500/10 p-4 hover:bg-red-500/20 transition-colors"
                data-testid="dashboard-promo-reminder">
                <Tag className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-200 text-sm">
                  {stats.days_since_last_promo == null ? "Aucune promotion lancée pour l'instant" : `Aucune promo depuis ${stats.days_since_last_promo} jours`} — c'est le moment de déstocker une sélection de parfums.
                </p>
              </Link>
            )}
            {hasPermission('promo') && stats?.promo_running && (
              <Link to="/admin/promotions" className="mb-6 flex items-center gap-3 border border-emerald-500/30 bg-emerald-500/5 p-4 hover:bg-emerald-500/10 transition-colors" data-testid="dashboard-promo-running">
                <Tag className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <p className="text-emerald-200 text-sm">Promo en cours : « {stats.promo_running.name} » ({stats.promo_running.products} produits) jusqu'au {new Date(stats.promo_running.ends_at).toLocaleDateString('fr-FR')}.</p>
              </Link>
            )}

            {hasPermission('users') && stats?.recovery_requests_pending > 0 && (
              <Link to="/admin/utilisateurs"
                className="mb-6 flex items-center gap-3 border border-amber-500/40 bg-amber-500/10 p-4 hover:bg-amber-500/20 transition-colors"
                data-testid="dashboard-recovery-alert">
                <LifeBuoy className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <p className="text-amber-200 text-sm">
                  {stats.recovery_requests_pending} client(s) ont oublié leur mot de passe — générez-leur un accès temporaire en un clic.
                </p>
              </Link>
            )}

            {hasPermission('products') && stats?.preorders_due > 0 && (
              <Link to="/admin/precommandes"
                className="mb-6 flex items-center gap-3 border border-amber-500/40 bg-amber-500/10 p-4 hover:bg-amber-500/20 transition-colors"
                data-testid="dashboard-preorders-due-alert">
                <CalendarClock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <p className="text-amber-200 text-sm">
                  {stats.preorders_due} arrivage(s) fournisseur attendu(s) aujourd'hui ou en retard — validez la réception pour mettre les produits en ligne.
                </p>
              </Link>
            )}

            <SlowMovingSection data={slowMoving} isAdmin={isAdmin} />

            <StatsGrid stats={stats} isAdmin={isAdmin} />

            {/* Quick action grid — filtered by employee permissions (Phase 38) */}
            <div className={`grid gap-4 mb-8 grid-cols-2 lg:grid-cols-4 xl:grid-cols-6`}>
              {hasPermission('products') && (
                <AdminQuickLink to="/admin/produits" icon={Package}
                  title="Produits" hint="Gérer le catalogue" testid="admin-products-link" />
              )}
              {hasPermission('promo') && (
                <AdminQuickLink to="/admin/promotions" icon={Tag}
                  title="Promotions" hint="Déstockage : nouveaux prix sur une période"
                  testid="admin-promotions-link" />
              )}
              {hasPermission('featured') && (
                <AdminQuickLink to="/admin/newsletter" icon={Megaphone}
                  title="Newsletter" hint="Affiches nouveautés & diffusion WhatsApp"
                  testid="admin-newsletter-link" />
              )}
              {hasPermission('products') && (
                <AdminQuickLink to="/admin/precommandes" icon={CalendarClock}
                  title="Précommandes" hint="Arrivages fournisseurs"
                  badge={stats?.preorders_due > 0 ? stats.preorders_due : null}
                  testid="admin-preorders-link" />
              )}
              {hasPermission('products') && (
                <AdminQuickLink to="/admin/inspirations" icon={Sparkles}
                  title="Inspirations IA" hint="Dupes & notes olfactives"
                  testid="admin-dupes-link" />
              )}
              {hasPermission('orders') && (
                <AdminQuickLink to="/admin/commandes" icon={ShoppingCart}
                  title="Commandes" hint="Traiter les commandes" testid="admin-orders-link" />
              )}
              {hasPermission('pos') && (
                <AdminQuickLink to="/admin/devis" icon={FileSignature}
                  title="Devis" hint="Préparer & valider"
                  testid="admin-quotes-link" />
              )}
              {hasPermission('delivery_notes') && (
                <AdminQuickLink to="/admin/bons" icon={FileText}
                  title="Bons de livraison" hint="Préparations en cours" testid="admin-notes-link" />
              )}
              {hasPermission('featured') && (
                <AdminQuickLink to="/admin/coups-de-coeur" icon={Heart}
                  title="Coups de cœur" hint="Sélection de la semaine"
                  testid="admin-featured-link" />
              )}
              {hasPermission('featured') && (
                <AdminQuickLink to="/admin/banniere" icon={GalleryHorizontal}
                  title="Bannière accueil" hint="Carrousel défilant"
                  testid="admin-banner-link" />
              )}
              {(hasPermission('credit_customers') || hasPermission('credit_suppliers') || hasPermission('treasury')) && (
                <AdminQuickLink to="/admin/comptabilite" icon={Coins}
                  title="Comptabilité" hint="Dettes & trésorerie"
                  testid="admin-credit-link" />
              )}
              {hasPermission('promo') && (
                <AdminQuickLink to="/admin/promo" icon={DollarSign}
                  title="Codes Promo" hint="Gérer les promotions" testid="admin-promo-link" />
              )}
              {hasPermission('users') && (
                <AdminQuickLink to="/admin/utilisateurs" icon={Users}
                  title="Utilisateurs" hint="Comptes & Employés"
                  badge={stats?.pending_users > 0 ? stats.pending_users : null}
                  testid="admin-users-link" />
              )}
              {hasPermission('analytics') && (
                <AdminQuickLink to="/admin/analytics" icon={BarChart3}
                  title="Statistiques" hint="CA, marges, KPI"
                  testid="admin-analytics-link" />
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {isAdmin && <RecentOrdersPanel orders={stats?.recent_orders || []} />}
              <LowStockPanel products={stats?.low_stock_products || []} wide={!isAdmin} />
            </div>
          </>
        )}
      </div>

      <CataloguePdfModal
        open={showCatalogueModal}
        onClose={() => setShowCatalogueModal(false)}
      />
    </div>
  );
};

export default AdminDashboard;
